import { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";

import {
	clearSearchError,
	fetchSearchFromMapCenter,
	pushSearchFromMapMoveByCoordinates
} from "store/modules/search";
import { searchPageSelector } from "store/selectors/SearchPage";
import { getDistance } from "components/google-maps/services/geoUtils";
import { PATHS } from "config/paths";
import { decodeQueryStringValue } from "utils/search/buildSearchLink";

import {
	MAP_MOVE_MIN_DISTANCE_MILES,
	PAN_UP_PX,
	MEDIUM_BREAKPOINT_MAX_PX
} from "./constants";

function scrollToSearchPage() {
	const el = document.getElementById("searchPage");
	if (el) {
		el.scrollIntoView({ behavior: "smooth", block: "start" });
	}
}

// Scroll card toward center without scrolling the map row out of view
function scrollToFacilityCardKeepingMapInView(cardEl) {
	if (!cardEl || typeof globalThis.window === "undefined") return;
	const vh = globalThis.innerHeight;
	const y = globalThis.scrollY ?? globalThis.pageYOffset;
	const card = cardEl.getBoundingClientRect();
	const desired = y + card.top + card.height / 2 - vh / 2;
	const row = document.querySelector(".search-page-v2 .page-layout");
	const max = row
		? y + row.getBoundingClientRect().top + row.offsetHeight - vh
		: desired;
	globalThis.scrollTo({
		top: Math.max(0, Math.min(desired, max)),
		behavior: "smooth"
	});
}

/** Map pan-to-search, loader state, and pin selection for SearchPageV2. */
export default function useMapSearch() {
	const { location } = useSelector(searchPageSelector);
	const searchLoading = useSelector((state) => state.search.loading);
	const dispatch = useDispatch();
	const { search, pathname } = useLocation();
	const navigate = useNavigate();
	const queryParams = useMemo(() => decodeQueryStringValue(search), [search]);

	const [selectedFacilityId, setSelectedFacilityId] = useState(null);
	const [hoveredFacilityId, setHoveredFacilityId] = useState(null);
	const [pinSelected, setPinSelected] = useState(false);
	const [isOverlayClosing, setIsOverlayClosing] = useState(false);
	const [searchFromMove, setSearchFromMove] = useState(false);
	const [showMapLoader, setShowMapLoader] = useState(false);
	const [mapMoveMessage, setMapMoveMessage] = useState(null);

	const mapRef = useRef(null);
	const googleMapsRef = useRef(null);
	const prevSearchLoadingRef = useRef(false);
	// Ignore the very first map move event fired on initial render so
	// we don't immediately trigger a new search and loader flash.
	const hasHandledFirstMoveRef = useRef(false);
	const revertToLastPlaceRef = useRef(null);
	// Skip the next onMove when we pan to center a clicked marker (avoid triggering search)
	const skipNextMoveRef = useRef(false);

	const searchError = useSelector((state) => state.search.error);

	// Clear map loader when search (from map move) finishes; revert map on API error
	useEffect(() => {
		const wasLoading = prevSearchLoadingRef.current;
		prevSearchLoadingRef.current = searchLoading;
		if (showMapLoader && searchFromMove && wasLoading && !searchLoading) {
			if (
				searchError &&
				typeof revertToLastPlaceRef.current === "function"
			) {
				revertToLastPlaceRef.current();
			} else {
				setShowMapLoader(false);
				setSearchFromMove(false);
			}
		}
	}, [searchLoading, showMapLoader, searchFromMove, searchError]);

	const handleMapMove = ({ latitude, longitude }) => {
		if (skipNextMoveRef.current) {
			skipNextMoveRef.current = false;
			return;
		}
		// Skip the first onMove call after map initialization; this is usually
		// the map fitting bounds to the initial search results, not a user move.
		if (!hasHandledFirstMoveRef.current) {
			hasHandledFirstMoveRef.current = true;
			return;
		}

		const lat = Number(latitude);
		const lng = Number(longitude);
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

		// Only run search when map center has moved at least the minimum distance
		const currentLat = location?.latitude;
		const currentLng = location?.longitude;
		const distanceUnderThreshold =
			currentLat != null &&
			currentLng != null &&
			getDistance(currentLat, currentLng, lat, lng) <
				MAP_MOVE_MIN_DISTANCE_MILES;
		if (distanceUnderThreshold) {
			return;
		}

		setSearchFromMove(true);
		setShowMapLoader(true);
		setMapMoveMessage(null);

		const revertToLastPlace = () => {
			dispatch(clearSearchError());
			setShowMapLoader(false);
			setSearchFromMove(false);
			setMapMoveMessage(
				"Location could not be updated. Please try again."
			);
			const canRevert =
				mapRef.current &&
				googleMapsRef.current &&
				location?.latitude != null &&
				location?.longitude != null;
			if (canRevert) {
				const prevCenter = new googleMapsRef.current.maps.LatLng(
					location.latitude,
					location.longitude
				);
				mapRef.current.setCenter(prevCenter);
			}
			navigate(`${pathname}${search || ""}#searchPage`, {
				replace: true
			});
		};
		revertToLastPlaceRef.current = revertToLastPlace;

		// Map-drag search: use map center coordinates only (no Geocode/Places API).
		// Push URL with current pathname (preserves /self-storage/.../amenity/ on CLP) and new location params.
		const isSearchOrCLP =
			pathname.replace(/\/$/, "") === PATHS.SEARCH ||
			pathname.startsWith("/self-storage/");
		if (isSearchOrCLP) {
			dispatch(
				pushSearchFromMapMoveByCoordinates({
					pathname,
					queryParams,
					latitude: lat,
					longitude: lng,
					pushUrl: (url) => navigate(url)
				})
			);
		} else {
			dispatch(fetchSearchFromMapCenter(lat, lng));
		}
		scrollToSearchPage();
	};

	const handleMapLocationClick = (clickedLocation) => {
		if (clickedLocation?.id == null) return;
		if (clickedLocation.id === selectedFacilityId) {
			setIsOverlayClosing(true);
			return;
		}
		setSelectedFacilityId(clickedLocation.id);
		setPinSelected(true);

		if (mapRef.current && googleMapsRef.current) {
			skipNextMoveRef.current = true;
			const { latitude, longitude } = clickedLocation;
			const latLng = new googleMapsRef.current.maps.LatLng(
				latitude,
				longitude
			);
			const map = mapRef.current;
			map.panTo(latLng);
			// Mobile: pan up so marker stays above the card overlay; desktop: keep marker centered
			if (
				globalThis.matchMedia(
					`(max-width: ${MEDIUM_BREAKPOINT_MAX_PX}px)`
				).matches
			) {
				map.panBy(0, PAN_UP_PX);
			}
		}

		const el = document.getElementById(
			`facility-card-${clickedLocation.id}`
		);
		if (el) {
			scrollToFacilityCardKeepingMapInView(el);
		}
	};

	return {
		handleMapMove,
		handleMapLocationClick,
		showMapLoader,
		mapMoveMessage,
		searchFromMove,
		setSearchFromMove,
		selectedFacilityId,
		setSelectedFacilityId,
		hoveredFacilityId,
		setHoveredFacilityId,
		mapRef,
		googleMapsRef,
		pinSelected,
		setPinSelected,
		isOverlayClosing,
		setIsOverlayClosing
	};
}
